"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getEfficiency = exports.getLongTermFinancialSavings = exports.getYearlyFinancialSavings = exports.getPaybackTime = exports.resolveEnergyPrices = void 0;
const constants_1 = require("../constants");
const energy_1 = require("./energy");
const { calculations: calcs } = constants_1.constants;
// Makes all properties required on an CustomEnergyPrices object by settings missing values to their default from constants.
function resolveEnergyPrices(prices) {
    return {
        gas: prices.gas ?? constants_1.constants.energyPrices.gas,
        electricity: prices.electricity ?? constants_1.constants.energyPrices.electricity,
        discountedElectricity: prices.discountedElectricity ?? constants_1.constants.energyPrices.discountedElectricity,
    };
}
exports.resolveEnergyPrices = resolveEnergyPrices;
/**
 * Payback time, including inflation. Note: Capped at `maxTerm` when over `maxTerm` argument (100 by default)
 * @param energyDelta
 * @param investment
 * @param term
 * @returns Amount of years until the amount of money saved is greater or equal than the investment
 */
function getPaybackTime(consumption, energyDelta, investment, energyPrices = constants_1.constants.energyPrices, maxTerm = 100) {
    // simple reference version: investment / savingsMoney
    // with inflation:
    // - investment = savingsMoney * (inflation^payBackTime)
    // - payBackTime = log_inflation(investment / savingsMoney)
    // but we have two of these equations:
    // investment = elecSavings * (elecInflation^payBackTime) + gasSavings * (gasInflation^payBackTime)
    // y = a * p^x + b * q^x
    // y = ???
    // (this isn't even correct I think - should be an integration)
    // let's just do a loop
    // TODO: degradationFactor here too
    const { electricity, gas, total } = getYearlyFinancialSavings(consumption, energyDelta, energyPrices);
    // For negative savings, payback time is infinite
    if (total <= 0) {
        return maxTerm;
    }
    // let debugLog = '';
    let totalSaved = 0;
    for (let year = 0; year < maxTerm; year++) {
        const yearlySaved = electricity * Math.pow(calcs.electricityInflation, year) +
            gas * Math.pow(calcs.gasInflation, year);
        totalSaved += yearlySaved;
        // prettier-ignore
        // debugLog += `Jaar ${year + 1}: Bespaard: ${yearlySaved.toFixed(2,)} (totaal: ${totalSaved.toFixed(2)})\n`;
        if (totalSaved >= investment) {
            // overshot the investment, add the fractional part of the year that it took extra (not subtracting because we start at year 0)
            const fraction = (yearlySaved - (totalSaved - investment)) / yearlySaved;
            const paybackTime = year + fraction;
            // console.debug(debugLog);
            return isFinite(paybackTime) ? paybackTime : maxTerm; // could be NaN for invalid input (e.g. empty energy delta)
        }
    }
    return maxTerm;
}
exports.getPaybackTime = getPaybackTime;
/**
 * Computes the yearly energy savings given the current consumption and the new situation (the energyDelta: changes in consumption/production)
 * Essentially just multiplies the energy savings with their prices, but also includes a check for discounted electricity price for high consumptions.
 * @param consumption The current consumption of the entity (e.g. House)
 * @param param1 The energyDelta: Change in consumption, e.g. due to the installation of an energy saving product
 */
function getYearlyFinancialSavings(consumption, ed, energyPrices = constants_1.constants.energyPrices) {
    const prices = resolveEnergyPrices(energyPrices);
    const gasMoney = ed.gasConsumption * prices.gas; // gas is straightforward
    // The total electricity consumption in the new situation
    const newElectricityConsumption = consumption.electricity - ed.electricityConsumption;
    // Split total electricity consumption into regular and discounted electricity.
    const discountedElectricity = (0, energy_1.calculateDiscountedElectricity)(newElectricityConsumption, ed.electricityProduction);
    const regularElectricity = ed.electricityProduction + ed.electricityConsumption - discountedElectricity;
    const electricityMoney = regularElectricity * prices.electricity + discountedElectricity * prices.discountedElectricity;
    return {
        gas: isFinite(gasMoney) ? gasMoney : 0,
        electricity: isFinite(electricityMoney) ? electricityMoney : 0,
        total: isFinite(gasMoney + electricityMoney) ? gasMoney + electricityMoney : 0,
    };
}
exports.getYearlyFinancialSavings = getYearlyFinancialSavings;
/**
 * Computes the savings in euros across the given amount of years (30 by default), including inflation
 * @param energyDelta
 * @param term Amount of years to take into account
 */
function getLongTermFinancialSavings(consumption, energyDelta, term = calcs.savingsTerm, energyPrices = constants_1.constants.energyPrices) {
    // TODO: Degradation factor for PV?
    // zonnepanelen.net says 0.7% per year https://www.zonnepanelen.net/uitleg-tool-terugverdientijd-zonnepanelen-berekenen/
    const { electricity, gas } = getYearlyFinancialSavings(consumption, energyDelta, energyPrices);
    // Total savings: sum money saved every year, with inflation
    const totalSavings = [...new Array(term)]
        .map((_, i) => i)
        .reduce((sum, year) => sum +
        electricity * Math.pow(calcs.electricityInflation, year) + // * degradationFactor
        gas * Math.pow(calcs.gasInflation, year), 0);
    return isFinite(totalSavings) ? totalSavings : 0;
}
exports.getLongTermFinancialSavings = getLongTermFinancialSavings;
/**
 * Standaard Rekenmethode Rendementen
 * Returns what percentage of the total investment is paid back each year
 * https://www.milieucentraal.nl/energie-besparen/energiesubsidies-en-leningen/rendement-energiebesparing/
 * @param energyDelta Amount of electricity saved per year
 * @param investment Total investment (incl. any subsidies)
 * @param term Amount of years to take into account (default: 30, "looptijd")
 * @returns Yearly efficiency in percentage [0;100]
 */
function getEfficiency(consumption, energyDelta, investment, energyPrices = constants_1.constants.energyPrices, term = calcs.savingsTerm) {
    // Old implementation: (Math.pow((savingsMoney * term * Math.pow(inflation, term)) / investment, 1 / term) - 1) * 100;
    // TODO: Maintenance costs. E.g. new inverter after 15 years for PV, PV lasts only last ~25 years
    // looptijd, levensduur, spaarrente, etc.
    const maintenanceCosts = 0;
    const totalCost = investment + maintenanceCosts;
    const totalSavings = getLongTermFinancialSavings(consumption, energyDelta, term, energyPrices);
    // [jaarlijks  rendement] = ( (totale opbrengst – totale uitgaven) / aanschafkosten) (tot de macht (1 / looptijd)) -1
    const efficiency = 100 * (Math.pow((totalSavings - totalCost) / investment, 1 / term) - 1);
    return isFinite(efficiency) ? efficiency : 0; // fallback for NaN or Infinity
}
exports.getEfficiency = getEfficiency;
/**
 * Payback time, including inflation. Note: Capped at `maxTerm` when over `maxTerm` argument (100 by default)
 * @param energyDelta
 * @param investment
 * @param term
 * @returns Amount of years until the amount of money saved is greater or equal than the investment
 */
// export function getPaybackTime(
//   consumption: IConsumption,
//   energyDelta: IEnergyDelta,
//   investment: number,
//   energyPrices = constants.energyPrices as CustomEnergyPrices,
//   maxTerm = 100,
// ) {
//   // simple reference version: investment / savingsMoney
//   // with inflation:
//   // - investment = savingsMoney * (inflation^payBackTime)
//   // - payBackTime = log_inflation(investment / savingsMoney)
//   // but we have two of these equations:
//   // investment = elecSavings * (elecInflation^payBackTime) + gasSavings * (gasInflation^payBackTime)
//   // y = a * p^x + b * q^x
//   // y = ???
//   // (this isn't even correct I think - should be an integration)
//   // let's just do a loop
//   // TODO: degradationFactor here too
//   const { electricity, gas, total } = getYearlyFinancialSavings(
//     consumption,
//     energyDelta,
//     energyPrices,
//   );
//   // For negative savings, payback time is infinite
//   if (total <= 0) {
//     return maxTerm;
//   }
//   // eslint-disable-next-line prettier/prettier
//   let debugLog = `Jaarlijkse besparing: elektriciteit: ${electricity.toFixed(2)}, gas: ${
//     gas.toFixed(2)}\nEnergieprijzen: elektriciteit: ${energyPrices.electricity}, gas: ${energyPrices.gas}\n`;
//   let totalSaved = 0;
//   for (let year = 0; year < maxTerm; year++) {
//     const yearlySaved =
//       electricity * Math.pow(calcs.electricityInflation, year) +
//       gas * Math.pow(calcs.gasInflation, year);
//     totalSaved += yearlySaved;
//     // prettier-ignore
//     debugLog += `  Jaar ${year + 1}: Bespaard: ${yearlySaved.toFixed(2)} (totaal: ${totalSaved.toFixed(2)})\n`;
//     if (totalSaved >= investment) {
//       // overshot the investment, add the fractional part of the year that it took extra (not subtracting because we start at year 0)
//       const fraction = (yearlySaved - (totalSaved - investment)) / yearlySaved;
//       const paybackTime = year + fraction;
//       console.debug(debugLog);
//       return isFinite(paybackTime) ? paybackTime : maxTerm; // could be NaN for invalid input (e.g. empty energy delta)
//     }
//   }
//   return maxTerm;
// }
